import { captureSelectionState, decoupleReference, getAbsoluteOffset, getAdjacentBlock, getBlockContentElement, getBlockModelById, getContentModelByNode, getParentBlock, getParentElement, getSelectedRange, getTargetBlock, isCursorAtEdge, isListTypeBlock, isNonMergableBlock, setCursorPosition } from '../../common/utils/index';
import { findClosestParent, getElementRect } from '../../common/utils/dom';
import * as constants from '../../common/constant';
import { events, actionType } from '../../common/constant';
import { BlockType } from '../../models/enums';
import { DeletionType } from '../../common/enums';
/**
 * Manages all event handlers for the BlockEditor component
 * This class centralizes event handling logic and provides a clean interface
 * for wiring and unwiring events across the editor
 */
export class EventAction {
    /**
     * Creates a new EventAction instance
     *
     * @param {BlockManager} manager The parent BlockManager instance
     */
    constructor(manager) {
        this.parent = manager;
        this.wireGlobalEvents();
    }
    /**
     * Wires up all global event handlers for the editor
     *
     * @returns {void}
     * @hidden
     */
    wireGlobalEvents() {
        // Document events
        this.parent.observer.on('selectionchange', this.handleEditorSelection, this);
        this.parent.observer.on('documentClick', this.handleDocumentClickActions, this);
        this.parent.observer.on('mousemove', this.handleMouseMoveActions, this);
        this.parent.observer.on('resize', this.handleWindowResize, this);
        // Editor events
        this.parent.observer.on('mouseup', this.handleMouseUpActions, this);
        this.parent.observer.on('mousedown', this.handleMouseDownActions, this);
        this.parent.observer.on('input', this.handleEditorInputActions, this);
        this.parent.observer.on('keydown', this.handleKeydownActions, this);
        this.parent.observer.on('clipboardAction', this.clipboardActionHandler, this);
        this.parent.observer.on('wireUnWireDragEvents', this.wireUnWireDragEvents, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    /**
     * Unwires all global event handlers for the editor
     *
     * @returns {void}
     * @hidden
     */
    unWireGlobalEvents() {
        // Document events
        this.parent.observer.off('selectionchange', this.handleEditorSelection);
        this.parent.observer.off('documentClick', this.handleDocumentClickActions);
        this.parent.observer.off('mousemove', this.handleMouseMoveActions);
        this.parent.observer.off('resize', this.handleWindowResize);
        // Editor events
        this.parent.observer.off('mouseup', this.handleMouseUpActions);
        this.parent.observer.off('mousedown', this.handleMouseDownActions);
        this.parent.observer.off('input', this.handleEditorInputActions);
        this.parent.observer.off('keydown', this.handleKeydownActions);
        this.parent.observer.off('clipboardAction', this.clipboardActionHandler);
        this.parent.observer.off('wireDragEvents', this.wireUnWireDragEvents);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    handleEditorSelection() {
        const range = this.parent.nodeSelection ? this.parent.nodeSelection.getRange() : null;
        if (!range) {
            return;
        }
        const isMoreThanSingleSelection = (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset);
        if (isMoreThanSingleSelection && this.parent.rootEditorElement.contains(range.commonAncestorContainer)) {
            this.parent.isEntireEditorSelected = this.parent.nodeSelection.checkIsEntireEditorSelected();
        }
    }
    handleDocumentClickActions(clickEvent) {
        if (!this.parent.rootEditorElement.contains(clickEvent.target)
            && (this.parent.floatingIconAction.floatingIconContainer
                && !this.parent.floatingIconAction.floatingIconContainer.contains(clickEvent.target))) {
            this.parent.floatingIconAction.hideFloatingIcons();
        }
        this.parent.selectionOverlay.clearSelectionOverlay();
        this.parent.isEntireEditorSelected = false;
        this.togglePopupsOnDocumentClick(clickEvent);
    }
    handleMouseMoveActions(moveEvent) {
        if (this.parent.contextMenuModule.isPopupOpen() || this.parent.blockActionMenuModule.isPopupOpen()) {
            return;
        }
        const blockElement = moveEvent.target.closest('.' + constants.BLOCK_CLS);
        if (blockElement) {
            if (blockElement !== this.parent.currentHoveredBlock) {
                if (this.parent.currentHoveredBlock) {
                    this.parent.floatingIconAction.hideFloatingIcons();
                }
                this.parent.currentHoveredBlock = blockElement;
                this.parent.floatingIconAction.showFloatingIcons(this.parent.currentHoveredBlock);
            }
        }
        else if (this.parent.currentHoveredBlock) {
            if (this.parent.floatingIconAction.floatingIconContainer
                && !this.parent.floatingIconAction.floatingIconContainer.contains(moveEvent.target)) {
                this.parent.floatingIconAction.hideFloatingIcons();
                this.parent.currentHoveredBlock = null;
            }
        }
    }
    handleMouseUpActions(mouseEvent) {
        if (this.parent.readOnly || (mouseEvent.target.tagName === 'TD')) {
            return;
        }
        this.parent.selectionOverlay.clearSelectionOverlay();
        const range = getSelectedRange();
        const startContainerParent = range ? getParentElement(range.startContainer) : null;
        const target = (startContainerParent || mouseEvent.target);
        const blockElement = target.closest('.' + constants.BLOCK_CLS);
        if (blockElement && (this.parent.currentFocusedBlock !== blockElement)) {
            this.parent.togglePlaceholder(this.parent.currentFocusedBlock, false);
            this.parent.setFocusToBlock(blockElement);
            this.parent.togglePlaceholder(this.parent.currentFocusedBlock, true);
            this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
            if (blockElement.innerText.length === 0) {
                setCursorPosition(getBlockContentElement(blockElement), 0);
            }
        }
        setTimeout(() => {
            const isPopupInteracted = this.parent.inlineToolbarModule
                && this.parent.inlineToolbarModule.popupObj.element.contains(mouseEvent.target);
            if (!isPopupInteracted) {
                this.handleTextSelection(mouseEvent);
            }
        });
    }
    handleMouseDownActions(mouseEvent) {
        this.parent.isEntireEditorSelected = false;
        this.parent.selectionOverlay.clearSelectionOverlay();
        if (this.parent.readOnly) {
            return;
        }
        const blockElement = mouseEvent.target.closest('.' + constants.BLOCK_CLS);
        if (blockElement && (this.parent.currentFocusedBlock !== blockElement)) {
            if (blockElement.innerText.length === 0) {
                setCursorPosition(getBlockContentElement(blockElement), 0);
            }
        }
    }
    handleEditorInputActions(inputEvent) {
        if (inputEvent.target && (inputEvent.target.closest('.e-uploader') || inputEvent.target.closest('.e-embed-url-input'))) {
            return;
        }
        this.parent.selectionOverlay.clearSelectionOverlay();
        const focusedBlk = this.parent.currentFocusedBlock;
        const tableBlock = focusedBlk ? findClosestParent(focusedBlk, '.e-table-block') : null;
        if (tableBlock) {
            const range = getSelectedRange();
            const tableEle = tableBlock.querySelector('table');
            const uiManager = this.parent.blockRenderer.tableRenderer.getManager(tableBlock.id);
            const cell = (findClosestParent(range.startContainer, 'td') || findClosestParent(range.startContainer, 'th'));
            uiManager.removeRowColSelection(tableEle);
            uiManager.hideRowGripper();
            uiManager.hideAllPinnedColBars();
            this.parent.tableService.removeCellFocus(tableEle);
            if (cell) {
                this.parent.tableService.addCellFocus(cell);
            }
        }
        this.processEntireEditorSelection();
        this.updateUIAfterInput(inputEvent);
        this.filterSlashCommandOnUserInput();
        if (this.processFormattingActions(inputEvent)) {
            return;
        }
        this.throttleContentUpdate(inputEvent);
    }
    processEntireEditorSelection() {
        if (this.parent.isEntireEditorSelected) {
            const editorBlocks = this.parent.getEditorBlocks();
            const allBlocks = editorBlocks.map((block) => decoupleReference(block));
            this.parent.setFocusToBlock(this.parent.blockContainer.firstElementChild);
            this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
            editorBlocks.splice(1);
            this.parent.stateManager.updateManagerBlocks();
            this.parent.isEntireEditorSelected = false;
            this.parent.undoRedoAction.pushActionIntoUndoStack({
                action: actionType.multipleBlocksDeleted,
                oldBlockModel: editorBlocks[0],
                data: {
                    deletedBlocks: allBlocks,
                    deletionType: DeletionType.Entire
                }
            });
        }
    }
    updateUIAfterInput(inputEvent) {
        if (this.parent.inlineToolbarModule) {
            this.parent.inlineToolbarModule.hideInlineToolbar(inputEvent);
        }
        this.parent.togglePlaceholder(this.parent.currentFocusedBlock, true);
        this.parent.floatingIconAction.hideDragIconForEmptyBlock(this.parent.currentFocusedBlock);
        const blockContent = getBlockContentElement(this.parent.currentFocusedBlock);
        if (blockContent && blockContent.textContent.length <= 1) {
            this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
        }
    }
    processFormattingActions(inputEvent) {
        const isInsertText = inputEvent.inputType === 'insertText';
        if ((isInsertText && this.parent.formattingAction.activeInlineFormats
            && this.parent.formattingAction.activeInlineFormats.size > 0)
            || this.parent.formattingAction.lastRemovedFormat) {
            return this.parent.formattingAction.handleTypingWithActiveFormats();
        }
        return false;
    }
    throttleContentUpdate(inputEvent) {
        clearTimeout(this.parent.updateTimer);
        this.parent.updateTimer = setTimeout(() => {
            const target = this.parent.currentFocusedBlock;
            this.parent.stateManager.updateContentOnUserTyping(target, inputEvent);
        }, 100);
    }
    handleTextSelection(event) {
        const range = this.parent.nodeSelection.getRange();
        // this.parent.nodeSelection.updateSelectionRangeOnUserModel();
        if (!range || range.toString().trim().length === 0) {
            this.parent.inlineToolbarModule.hideInlineToolbar(event);
            return;
        }
        const previousRange = this.parent.nodeSelection.getStoredRange();
        const selectionArgs = {
            event: event,
            // user: this.parent.users.find((user: UserModel) => user.id === this.parent.currentUserId),
            range: [range.startOffset, range.endOffset],
            previousRange: previousRange ? [previousRange.startOffset, previousRange.endOffset] : null
        };
        this.parent.observer.notify('selectionChanged', selectionArgs);
        this.parent.nodeSelection.storeCurrentRange();
        const rect = range.getBoundingClientRect();
        if (range && rect) {
            const parentBlock = getParentBlock(range.startContainer);
            if (parentBlock && parentBlock.classList.contains('e-block')) {
                this.parent.inlineToolbarModule.showInlineToolbar(range, event);
            }
            else {
                this.parent.inlineToolbarModule.hideInlineToolbar(event);
            }
        }
    }
    filterSlashCommandOnUserInput() {
        const blockElement = this.parent.currentFocusedBlock;
        if (this.parent.slashCommandModule.isPopupOpen() &&
            blockElement &&
            blockElement.innerText &&
            this.parent.isPopupOpenedOnAddIconClick) {
            const rect = getElementRect(blockElement);
            const targetEle = getBlockContentElement(blockElement) || blockElement;
            const xOffset = rect.left;
            const yOffset = rect.top + targetEle.offsetHeight;
            this.parent.slashCommandModule.filterCommands(blockElement.innerText, xOffset, yOffset);
        }
    }
    handleKeydownActions(keyEvent) {
        this.parent.previousSelection = captureSelectionState();
        if (!this.parent.currentFocusedBlock || !this.validateKeyEventProcessability(keyEvent) || ((keyEvent.target && keyEvent.target.closest('.e-embed-url-input')))) {
            return;
        }
        this.handleInlineTbarStates(keyEvent);
        if (this.isAnyPopupOpen()) {
            return;
        }
        /* Case where user selects multi blocks and then input a character */
        const range = getSelectedRange();
        const selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
        const isCharacterKey = keyEvent.key.length === 1 && !keyEvent.ctrlKey && !keyEvent.metaKey && !keyEvent.altKey;
        if (selectedBlocks && selectedBlocks.length > 1 && range && !range.collapsed && isCharacterKey) {
            this.parent.isEntireEditorSelected = this.parent.nodeSelection.checkIsEntireEditorSelected();
            if (this.parent.isEntireEditorSelected) {
                this.parent.blockCommand.handleEntireBlockDeletion();
            }
            else {
                this.parent.blockCommand.handleMultipleBlockDeletion(selectedBlocks, 'previous');
            }
            return;
        }
        this.processKeyboardShortcuts(keyEvent);
    }
    validateKeyEventProcessability(keyEvent) {
        const blockModel = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        const isUpDownArrows = ['ArrowUp', 'ArrowDown'].indexOf(keyEvent.key) !== -1;
        const isControlUpDownShift = keyEvent.ctrlKey && isUpDownArrows && keyEvent.shiftKey;
        return blockModel && !isControlUpDownShift;
    }
    handleInlineTbarStates(keyEvent) {
        const isArrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].indexOf(keyEvent.key) !== -1;
        const isLeftRightArrows = ['ArrowLeft', 'ArrowRight'].indexOf(keyEvent.key) !== -1;
        const isControlKey = keyEvent.ctrlKey || keyEvent.metaKey;
        const isShiftKey = keyEvent.shiftKey;
        if (keyEvent.key === 'Escape' || (!isControlKey && isArrowKeys && !isShiftKey)) {
            this.parent.inlineToolbarModule.hideInlineToolbar(keyEvent);
        }
        else if ((isControlKey && isLeftRightArrows && isShiftKey)) {
            this.showInlineToolbarWithDelay(keyEvent);
        }
    }
    showInlineToolbarWithDelay(keyEvent) {
        const inlineTbarPopup = document.querySelector('#' + this.parent.rootEditorElement.id + constants.INLINE_TBAR_POPUP_ID);
        if (!(inlineTbarPopup && inlineTbarPopup.classList.contains('e-popup-open'))) {
            setTimeout(() => {
                const range = getSelectedRange();
                if (range) {
                    this.parent.inlineToolbarModule.showInlineToolbar(range, keyEvent);
                }
            });
        }
    }
    processKeyboardShortcuts(keyEvent) {
        const blockElement = this.parent.currentFocusedBlock;
        const blockModel = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
        if (this.processListBlockEvents(keyEvent, blockElement, blockModel)) {
            return;
        }
        this.handleBlockKeyActions(keyEvent);
    }
    isAnyPopupOpen() {
        const mentionPopupId = `${this.parent.blockContainer.id}_popup`;
        const commandPopupElement = document.querySelector(`#${mentionPopupId}.e-blockeditor-command-menu`);
        const userMentionPopupElement = document.querySelector(`#${mentionPopupId}.e-blockeditor-user-menu`);
        const labelMentionPopupElement = document.querySelector(`#${mentionPopupId}.e-blockeditor-label-menu`);
        const blockModel = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        const actionPopupElement = this.parent.rootEditorElement.querySelector(`#${this.parent.rootEditorElement.id + constants.BLOCKACTION_POPUP_ID}`);
        const linkDialogElement = this.parent.rootEditorElement.querySelector(`#${this.parent.rootEditorElement.id + constants.LINKDIALOG_ID}`);
        const codeLanguagePopup = document.querySelector(`#${this.parent.rootEditorElement.id}_code-ddl_popup`);
        const notAllowedTypes = [BlockType.Code];
        return this.parent.slashCommandModule.isPopupOpen() || (commandPopupElement && commandPopupElement.classList.contains('e-popup-open')) ||
            (userMentionPopupElement && userMentionPopupElement.classList.contains('e-popup-open')) ||
            (labelMentionPopupElement && labelMentionPopupElement.classList.contains('e-popup-open')) ||
            (blockModel && notAllowedTypes.indexOf(blockModel.blockType) !== -1) ||
            (actionPopupElement && actionPopupElement.classList.contains('e-popup-open')) ||
            (linkDialogElement && linkDialogElement.classList.contains('e-popup-open') ||
                (codeLanguagePopup && codeLanguagePopup.classList.contains('e-popup-open')));
    }
    processListBlockEvents(keyEvent, blockElement, blockModel) {
        this.parent.listPlugin.handleListTriggerKey(keyEvent, blockElement, blockModel);
        const range = getSelectedRange();
        const selectedBlocks = range && range.toString().length > 0 ? this.parent.editorMethods.getSelectedBlocks() : [];
        const isSelectiveDeletions = this.parent.isEntireEditorSelected || (selectedBlocks && selectedBlocks.length > 1);
        if (blockModel && isListTypeBlock(blockModel.blockType) && !isSelectiveDeletions) {
            return this.parent.listPlugin.handleListKeyActions(keyEvent, blockElement);
        }
        return false;
    }
    handleBlockKeyActions(event) {
        const blockElement = this.parent.currentFocusedBlock;
        const contentElement = getBlockContentElement(blockElement);
        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight':
                this.handleArrowKeyActions(event, getSelectedRange(), blockElement);
                this.parent.isEntireEditorSelected = false;
                break;
            case 'Enter':
                this.processEnterKey(event, blockElement);
                break;
            case 'Backspace':
            case 'Delete':
                this.processBlockDeletions(event, blockElement, blockElement.getAttribute('data-block-type'), contentElement);
                break;
            case 'Tab':
                if (blockElement.closest(`.${constants.TABLE_BLOCK_CLS}`)) {
                    return;
                }
                this.processTabKey(event);
                break;
            case 'Home':
            case 'End': {
                if (!event.shiftKey) {
                    this.handleHomeEndKeyActions(event, blockElement);
                }
                break;
            }
        }
    }
    processEnterKey(event, blockElement) {
        this.parent.inlineToolbarModule.hideInlineToolbar();
        event.preventDefault();
        if (event.shiftKey) {
            this.handleLineBreaksOnBlock(blockElement);
        }
        else {
            this.handleNormalEnterKey();
        }
    }
    handleNormalEnterKey() {
        if (!getSelectedRange()) {
            return;
        }
        if (this.processSpecialContainerBlocks()) {
            return;
        }
        if (this.processIndentIfBlockEmpty()) {
            return;
        }
        this.parent.execCommand({ command: 'SplitBlock' });
        this.parent.isEntireEditorSelected = false;
    }
    processSpecialContainerBlocks() {
        const calloutBlock = this.parent.currentFocusedBlock.closest('.' + constants.CALLOUT_BLOCK_CLS);
        const toggleBlock = this.parent.currentFocusedBlock.closest('.' + constants.TOGGLE_BLOCK_CLS);
        const quoteBlock = this.parent.currentFocusedBlock.closest('.' + constants.QUOTE_BLOCK_CLS);
        if (calloutBlock) {
            return this.handleChildrenBlockExit('.' + constants.CALLOUT_BLOCK_CLS, '.' + constants.CALLOUT_CONTENT_CLS);
        }
        else if (toggleBlock) {
            return this.processToggleBlock(toggleBlock);
        }
        else if (quoteBlock) {
            return this.handleChildrenBlockExit('.' + constants.QUOTE_BLOCK_CLS, '.' + constants.QUOTE_CONTENT_CLS);
        }
        return false;
    }
    processToggleBlock(toggleBlock) {
        const blockModel = getBlockModelById(toggleBlock.id, this.parent.getEditorBlocks());
        const toggleHeader = findClosestParent(getSelectedRange().startContainer, '.e-toggle-header');
        const toggleContent = toggleBlock.querySelector('.' + constants.TOGGLE_CONTENT_CLS);
        if (toggleContent && toggleHeader && toggleContent.textContent === '') {
            this.parent.blockRenderer.collapsibleRenderer.updateCollapsibleBlockExpansion(this.parent.currentFocusedBlock, !blockModel.properties.isExpanded);
            setCursorPosition(toggleContent.querySelector('.' + constants.CONTENT_CLS), 0);
            this.parent.setFocusToBlock(this.parent.currentFocusedBlock.querySelector('.' + constants.BLOCK_CLS));
            return true;
        }
        return this.handleChildrenBlockExit('.' + constants.TOGGLE_BLOCK_CLS, '.' + constants.TOGGLE_CONTENT_CLS);
    }
    processIndentIfBlockEmpty() {
        const blockModel = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        if (this.parent.currentFocusedBlock.textContent.trim() === '' && blockModel.indent > 0) {
            this.parent.execCommand({
                command: 'IndentBlock',
                state: {
                    blockIDs: [blockModel.id],
                    shouldDecrease: true
                }
            });
            this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
            return true;
        }
        return false;
    }
    processBlockDeletions(event, blockElement, blockType, contentElement) {
        const mergeDirection = (event.key === 'Backspace') ? 'previous' : 'next';
        this.parent.inlineToolbarModule.hideInlineToolbar();
        // Handle multi-block / entire selection first
        const isDeletionPerformed = this.parent.blockCommand.handleSelectiveDeletions(event);
        if (isDeletionPerformed) {
            return;
        }
        // Direct deletion of selected Non-mergeable block
        if (isNonMergableBlock(blockElement)) {
            this.parent.execCommand({
                command: 'DeleteNonMergableBlock',
                state: { blockElement: blockElement }
            });
            event.preventDefault();
            return;
        }
        // Two-step delete for adjacent special blocks at boundary
        if (getSelectedRange() && isCursorAtEdge(contentElement, event.key === 'Backspace')) {
            const adjacentBlock = getTargetBlock(blockElement, mergeDirection);
            if (adjacentBlock) {
                const adjacentModel = getBlockModelById(adjacentBlock.id, this.parent.getEditorBlocks());
                const isSpecial = !!adjacentModel && [
                    BlockType.Code,
                    BlockType.Callout,
                    BlockType.Table,
                    BlockType.Image,
                    BlockType.Quote,
                    BlockType.CollapsibleHeading,
                    BlockType.CollapsibleParagraph,
                    BlockType.Divider
                ].indexOf(adjacentModel.blockType) !== -1;
                if (isSpecial) {
                    // First press: stage overlay
                    event.preventDefault();
                    this.parent.selectionOverlay.selectionOverlayInfo = { element: adjacentBlock, direction: mergeDirection };
                    this.parent.lastHighlightedBlockId = adjacentBlock.id;
                    if (this.parent.selectionOverlay) {
                        this.parent.selectionOverlay.show(adjacentBlock.id);
                    }
                    if (this.parent.nodeSelection) {
                        this.parent.nodeSelection.clearSelection();
                    }
                    return;
                }
            }
            // Fallback to normal deletion-at-cursor for block merging
            event.preventDefault();
            this.parent.execCommand({
                command: 'DeleteAtCursor',
                state: {
                    blockElement: blockElement,
                    mergeDirection: mergeDirection
                }
            });
            this.parent.isEntireEditorSelected = false;
            return;
        }
        // Second press: delete
        if (this.parent.selectionOverlay.selectionOverlayInfo && this.parent.selectionOverlay.selectionOverlayInfo.element) {
            event.preventDefault();
            const blockEle = this.parent.selectionOverlay.selectionOverlayInfo.element;
            this.parent.selectionOverlay.clearSelectionOverlay();
            this.parent.execCommand({
                command: 'DeleteNonMergableBlock',
                state: { blockElement: blockEle }
            });
            return;
        }
    }
    processTabKey(event) {
        this.parent.execCommand({
            command: 'IndentBlock',
            state: {
                blockIDs: this.parent.editorMethods.getSelectedBlocks().map((block) => block.id),
                shouldDecrease: event.shiftKey
            }
        });
        this.parent.isEntireEditorSelected = false;
        event.preventDefault();
    }
    handleHomeEndKeyActions(event, blockElement) {
        const contentElement = getBlockContentElement(blockElement);
        setCursorPosition(contentElement, (event.key === 'Home') ? 0 : contentElement.textContent.length);
    }
    handleLineBreaksOnBlock(blockElement) {
        const blockModel = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
        const oldBlock = decoupleReference(blockModel);
        const contentElement = getBlockContentElement(blockElement);
        const range = this.parent.nodeSelection.getRange();
        if (!range) {
            return;
        }
        const absoluteOffset = getAbsoluteOffset(contentElement, range.startContainer, range.startOffset);
        const contentModel = getContentModelByNode(range.startContainer, this.parent.getEditorBlocks());
        this.parent.blockService.applyLineBreak(range.startOffset, contentModel);
        this.parent.stateManager.updateManagerBlocks();
        this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                data: [{ block: blockModel, oldBlock: oldBlock }]
            } });
        setCursorPosition(contentElement, absoluteOffset + 1);
        this.parent.undoRedoAction.trackLineBreakActionForUndoRedo({
            blockId: blockModel.id,
            oldContent: oldBlock.content,
            newContent: decoupleReference(blockModel.content)
        });
    }
    handleChildrenBlockExit(parentSelector, contentSelector, deleteDirection = 'previous') {
        const parentBlock = this.parent.currentFocusedBlock.closest(parentSelector);
        const contentElement = parentBlock ? parentBlock.querySelector(contentSelector) : null;
        if (parentBlock && contentElement &&
            (this.parent.currentFocusedBlock.textContent.trim() === '') && contentElement.childElementCount > 1 &&
            (contentElement.lastElementChild === this.parent.currentFocusedBlock)) {
            this.parent.execCommand({ command: 'DeleteBlock', state: {
                    blockElement: this.parent.currentFocusedBlock,
                    mergeDirection: deleteDirection
                } });
            this.parent.currentFocusedBlock = parentBlock;
            this.parent.execCommand({ command: 'AddBlock', state: {
                    blockType: BlockType.Paragraph,
                    targetBlock: this.parent.currentFocusedBlock
                } });
            return true;
        }
        return false;
    }
    handleArrowKeyActions(event, range, blockElement) {
        this.parent.selectionOverlay.clearSelectionOverlay();
        const blockContentLength = blockElement.textContent.length;
        const key = event.key;
        const isUp = key === 'ArrowUp';
        const isDown = key === 'ArrowDown';
        const isLeft = key === 'ArrowLeft';
        const isRight = key === 'ArrowRight';
        const isAtStart = range && (range.startOffset === 0 && range.endOffset === 0);
        const isAtEnd = range && (range.startOffset === blockContentLength && range.endOffset === blockContentLength);
        const adjacentBlock = getAdjacentBlock(blockElement, (isUp || isLeft) ? 'previous' : 'next');
        if (!adjacentBlock) {
            return;
        }
        if (adjacentBlock && adjacentBlock.classList.contains(constants.TABLE_BLOCK_CLS)) {
            const tableEl = adjacentBlock.querySelector('table');
            let targetCell = null;
            if ((isUp || isDown) && tableEl) {
                if (isUp) {
                    // focus first data-cell of the last row
                    const lastDomRow = tableEl.rows.length - 1;
                    targetCell = tableEl.rows[lastDomRow].cells[1];
                }
                else {
                    // ArrowDown → first header TH if present, else first body cell
                    targetCell = tableEl.tHead ? tableEl.tHead.rows[0].cells[1] : tableEl.rows[0].cells[1];
                }
            }
            if (targetCell) {
                this.parent.tableService.addCellFocus(targetCell, true);
                event.preventDefault();
                return;
            }
        }
        const isAdjacentEmpty = adjacentBlock.textContent.length === 0;
        //Only prevent default behaviour when cursor at the ends, otherwise let the browser's default behaviour take over
        const isMovingAdjacentBlock = (isAtStart && (isLeft)) || (isAtEnd && (isRight)) || ((isUp || isDown) && isAdjacentEmpty);
        if (isMovingAdjacentBlock) {
            event.preventDefault();
            this.moveCursorToAdjacentBlock(adjacentBlock, key);
        }
        else {
            setTimeout(() => {
                if (!isMovingAdjacentBlock) {
                    const range = getSelectedRange();
                    const currentBlock = (range && range.startContainer.parentElement.closest('.' + constants.BLOCK_CLS));
                    if (currentBlock !== this.parent.currentFocusedBlock) {
                        this.parent.togglePlaceholder(this.parent.currentFocusedBlock, false);
                        this.parent.setFocusToBlock(currentBlock);
                        this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
                    }
                }
            });
        }
    }
    moveCursorToAdjacentBlock(adjacentBlock, key) {
        this.parent.togglePlaceholder(this.parent.currentFocusedBlock, false);
        this.parent.setFocusToBlock(adjacentBlock);
        setCursorPosition(getBlockContentElement(adjacentBlock), (key === 'ArrowLeft') ? adjacentBlock.textContent.length : 0);
        this.parent.togglePlaceholder(this.parent.currentFocusedBlock, true);
        this.parent.floatingIconAction.showFloatingIcons(this.parent.currentFocusedBlock);
    }
    togglePopupsOnDocumentClick(event) {
        const inlineTbarPopup = document.querySelector('#' + this.parent.rootEditorElement.id + constants.INLINE_TBAR_POPUP_ID);
        const blockActionPopup = document.querySelector('#' + this.parent.rootEditorElement.id + constants.BLOCKACTION_POPUP_ID);
        const isInlineTbarOpen = inlineTbarPopup && inlineTbarPopup.classList.contains('e-popup-open');
        const isBlockActionOpen = blockActionPopup && blockActionPopup.classList.contains('e-popup-open');
        const isColorPaletteClick = event.target.closest('.e-colorpicker-wrapper') !== null ||
            event.target.closest('.e-color-palette') !== null;
        if (!this.parent.inlineToolbarModule.popupObj.element.contains(event.target) && isInlineTbarOpen && !isColorPaletteClick) {
            this.parent.inlineToolbarModule.hideInlineToolbar(event);
        }
        if (!this.parent.blockActionMenuModule.popupObj.element.contains(event.target) && isBlockActionOpen) {
            this.parent.blockActionMenuModule.toggleBlockActionPopup(true, event);
        }
    }
    clipboardActionHandler(e) {
        if (this.parent.linkModule.isPopupOpen() || (e.target && e.target.closest('.e-embed-url-input'))) {
            return;
        }
        switch (e.type.toLowerCase()) {
            case 'cut':
                this.parent.observer.notify(events.cut, e);
                break;
            case 'copy':
                this.parent.observer.notify(events.copy, e);
                break;
            case 'paste':
                this.parent.observer.notify(events.paste, e);
                break;
        }
        this.parent.stateManager.updateManagerBlocks();
    }
    handleWindowResize() {
        if (this.parent.inlineToolbarModule && this.parent.inlineToolbarModule.isPopupOpen()) {
            const range = this.parent.nodeSelection.getRange();
            this.parent.popupRenderer.adjustPopupPositionRelativeToTarget(range, this.parent.inlineToolbarModule.popupObj);
        }
        // Reposition overlay on resize
        if (this.parent.selectionOverlay) {
            this.parent.selectionOverlay.reposition();
        }
    }
    wireUnWireDragEvents(options) {
        if (options.enable) {
            this.parent.dragAndDropAction.wireDragEvents();
        }
        else {
            this.parent.dragAndDropAction.unwireDragEvents();
        }
    }
    // Reposition handled by SelectionOverlay
    destroy() {
        this.parent.selectionOverlay.clearSelectionOverlay();
        this.unWireGlobalEvents();
    }
}
